import React from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { Empty } from './Empty';
import { CustomCardProps } from './CustomCard';

export interface CardSelectorProps<T, TKey> {
  items: T[];
  keySelector: (item: T) => TKey;
  onSelectionChange: (items: Set<TKey>) => void;
  selectedItems: Set<TKey>;
  emptyListPlaceholder: string;
  onCreateClicked?: (item: T) => void;
  render: (item: T) => React.ReactElement<CustomCardProps>;
}

const useItemSelectorStyles = makeStyles({
  cardWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
});

const CardSelector = <T, TKey>({
  items,
  onSelectionChange,
  selectedItems,
  emptyListPlaceholder,
  keySelector,
  render,
}: CardSelectorProps<T, TKey>) => {
  const styles = useItemSelectorStyles();

  const handleSelectionChange = (item: T, isSelected: boolean) => {
    const newSelected = new Set(selectedItems);
    const key = keySelector(item);

    if (newSelected.has(key) && !isSelected) {
      newSelected.delete(key);
    } else if (!newSelected.has(key) && isSelected) {
      newSelected.add(key);
    }

    onSelectionChange(newSelected);
  };

  if (items.length === 0)
    return (
      <Empty
        text={emptyListPlaceholder}
        style={{
          minHeight: '100px',
        }}
      />
    );

  return (
    <div className={styles.cardWrapper}>
      {items.map((item) => {
        const key = keySelector(item);

        const ItemCard = render(item);

        const ItemCard2 = React.cloneElement(ItemCard, {
          key: key.toString(),
          selected: selectedItems.has(key),
          onSelectionChange: (selected) => handleSelectionChange(item, selected),
        });

        return ItemCard2;
      })}
    </div>
  );
};

export default CardSelector;
